import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { AuthCard } from "@/components/auth/AuthCard";
import { FormField } from "@/components/auth/FormField";
import { PasswordField } from "@/components/auth/PasswordField";
import { OtpInput } from "@/components/auth/OtpInput";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";

const schema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z
    .string()
    .min(8, "At least 8 characters")
    .refine((v) => /[A-Z]/.test(v) && /[a-z]/.test(v) && /\d/.test(v), {
      message: "Mix uppercase, lowercase and a number",
    }),
});

type FormValues = z.infer<typeof schema>;

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { confirmForgot } = useAuth();
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: params.get("email") || "" },
  });

  const onSubmit = handleSubmit(async (values) => {
    if (code.length !== 6) {
      setCodeError("Enter the 6-digit code");
      return;
    }
    setSubmitting(true);
    try {
      await confirmForgot(values.email.trim().toLowerCase(), code, values.password);
      toast.success("Password reset. You can sign in now.");
      navigate("/auth/login", { replace: true });
    } catch (err) {
      const message = (err as { message?: string }).message || "Reset failed.";
      setError("password", { message });
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <AuthCard
      title="Set a new password"
      description="Enter the 6-digit code we emailed you and your new password."
      footer={
        <>
          Didn't get a code?{" "}
          <Link to="/auth/forgot" className="font-medium text-brand hover:underline">
            Request a new one
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
        <FormField id="email" label="Email" error={errors.email?.message}>
          <Input
            id="email"
            type="email"
            inputMode="email"
            autoComplete="email"
            aria-invalid={!!errors.email}
            {...register("email")}
          />
        </FormField>

        <div className="flex flex-col gap-1.5">
          <Label>Reset code</Label>
          <OtpInput
            value={code}
            onChange={(v) => {
              setCode(v);
              setCodeError(null);
            }}
            inputMode="numeric"
          />
          {codeError ? <p className="text-sm text-danger">{codeError}</p> : null}
        </div>

        <FormField id="password" label="New password" error={errors.password?.message}>
          <PasswordField
            id="password"
            autoComplete="new-password"
            aria-invalid={!!errors.password}
            {...register("password")}
          />
        </FormField>

        <Button type="submit" fullWidth loading={submitting}>
          Reset password
        </Button>
      </form>
    </AuthCard>
  );
}
